import { useState } from "react";
import { Pencil, Check, Plus, X, ListPlus, RotateCcw } from "lucide-react";

// The emergency word lists with an unmistakable Edit → change → Done flow (owner: "I wanted a damn
// edit option"). Read mode shows the words as chips; Edit mode adds × on every chip, an add box,
// a paste-a-list textarea and Reset to defaults. Every change marks the page dirty so the Save bar
// appears — nothing is stored until Save.
export function WordsEditor({ label, help, words, onChange, canEdit, defaults, testid, tone = "ink" }) {
  const [editing, setEditing] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [draft, setDraft] = useState("");
  const list = words || [];
  const add = () => {
    const parts = draft.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!parts.length) return;
    const seen = new Set(list.map((w) => w.toLowerCase()));
    onChange([...list, ...parts.filter((p) => !seen.has(p.toLowerCase()) && seen.add(p.toLowerCase()))]);
    setDraft("");
  };
  const remove = (i) => onChange(list.filter((_, k) => k !== i));
  const chipTone = tone === "red" ? "border-red-200 bg-red-50 text-red-800" : "border-sand-200 bg-white text-ink";
  return (
    <div data-testid={`${testid}-editor`} className="rounded-xl border border-sand-200 bg-sand-50/40 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <label className="text-xs font-bold uppercase tracking-wide text-ink/50">{label} <span className="font-semibold normal-case tracking-normal text-ink/40">· {list.length}</span></label>
          {help && <p className="mt-0.5 text-xs text-ink/45">{help}</p>}
        </div>
        {canEdit && (editing ? (
          <button type="button" data-testid={`${testid}-done-btn`} onClick={() => { add(); setEditing(false); setBulk(false); }}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-700">
            <Check className="h-3.5 w-3.5" /> Done editing
          </button>
        ) : (
          <button type="button" data-testid={`${testid}-edit-btn`} onClick={() => setEditing(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink">
            <Pencil className="h-3.5 w-3.5" /> Edit words
          </button>
        ))}
      </div>

      {bulk && editing ? (
        <textarea data-testid={testid} value={list.join("\n")} autoFocus
          onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
          rows={10} className="mt-3 w-full resize-y rounded-xl border border-sand-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
      ) : (
        <div data-testid={`${testid}-chips`} className="mt-3 flex flex-wrap gap-1.5">
          {list.length === 0 && <span className="text-xs text-ink/45">No words yet{canEdit ? " — click Edit words to add some." : "."}</span>}
          {list.map((w, i) => (
            <span key={`${w}-${i}`} data-testid={`${testid}-chip-${i}`}
              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${chipTone}`}>
              {w}
              {editing && (
                <button type="button" data-testid={`${testid}-remove-${i}`} onClick={() => remove(i)} aria-label={`Remove ${w}`}
                  className="-mr-1 rounded-full p-0.5 text-ink/40 hover:bg-ink/10 hover:text-red-700">
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {editing && (
        <div className="mt-3 space-y-2" data-testid={`${testid}-edit-tools`}>
          {!bulk && (
            <div className="flex items-center gap-2">
              <input data-testid={`${testid}-add-input`} value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
                placeholder="Add a word or phrase — Enter to add, commas for several"
                className="min-w-0 flex-1 rounded-full border border-sand-300 bg-white px-3 py-1.5 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
              <button type="button" data-testid={`${testid}-add-btn`} onClick={add} disabled={!draft.trim()}
                className="inline-flex items-center gap-1 rounded-full border border-ink/20 bg-white px-3 py-1.5 text-xs font-semibold text-ink hover:border-ink disabled:opacity-40">
                <Plus className="h-3.5 w-3.5" /> Add
              </button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <button type="button" data-testid={`${testid}-bulk-toggle`} onClick={() => setBulk((b) => !b)} className="inline-flex items-center gap-1 font-semibold text-ink/60 hover:text-ink">
              <ListPlus className="h-3.5 w-3.5" /> {bulk ? "Back to word chips" : "Edit as a list (paste many at once)"}
            </button>
            {defaults && (
              <button type="button" data-testid={`${testid}-reset`} onClick={() => onChange([...defaults])} className="inline-flex items-center gap-1 font-semibold text-ink/60 hover:text-ink">
                <RotateCcw className="h-3.5 w-3.5" /> Reset to defaults
              </button>
            )}
            <span className="text-ink/45">Click × to remove a word. Your changes are kept when you press <b>Save</b>.</span>
          </div>
        </div>
      )}
    </div>
  );
}
